import { Injectable } from '@angular/core';
import { HttpErrorResponse } from '@angular/common/http';
import { Observable, of } from 'rxjs';
import { ApiError } from './error.models';
import { hasProperty } from '../../helpers/utils';
import { ToastService } from '../toast/toast.service';
import * as Sentry from '@sentry/angular';

@Injectable({
  providedIn: 'root',
})
export class ErrorHandlingService {
  constructor(private toastService: ToastService) {}

  handleError(error: HttpErrorResponse): ApiError {
    const isBusinessWarning = !!error?.error?.isWarning;

    const customError: ApiError = {
      isError: true,
      message: this.getErrorMessage(error),
      statusCode: error.status || 0,
      details: error.error ?? error.message,
      isWarning: !!error?.error?.isWarning,
      canForceDuplicate: !!error?.error?.canForceDuplicate,
      forceDuplicateParam: error?.error?.forceDuplicateParam,
    };

    // Ignorer les erreurs 404 sur les suppressions de company_users (suppressions idempotentes)
    if (error.status === 404 && error.url && error.url.includes('/api/company_users/')) {
      // Ne pas afficher de toast ni capturer dans Sentry pour ces erreurs
      return customError;
    }

    // Warning métier (doublon forçable) : géré par les composants, on évite
    // les toasts/Sentry globaux pour ne pas bruiter l'UX.
    if (!isBusinessWarning) {
      // Capturer l'erreur dans Sentry
      Sentry.captureException(error, {
        tags: {
          component: 'ErrorHandlingService',
          statusCode: error.status?.toString() || 'unknown'
        },
        extra: {
          url: error.url,
          method: error.error?.method || 'unknown',
          customError: customError
        }
      });

      this.toastService.show({
        message: `${customError.message}`,
        classname: 'bg-danger text-light',
        delay: 5000,
      });
    }

    return customError;
  }

  private getErrorMessage(error: HttpErrorResponse): string {
    if (error.error instanceof ErrorEvent) {
      // Erreur côté client
      return `${error.error.message}`;
    } else {
      if (
        error.status === 403 &&
        error.url?.includes('/api/sites') &&
        (
          error.error?.detail === 'Access Denied.' ||
          error.error?.message === 'Access Denied.' ||
          error.error?.['hydra:description'] === 'Access Denied.'
        )
      ) {
        return 'Vous ne pouvez pas créer un nouveau site. Seul un expert-comptable est autorisé à créer des sites.';
      }

      // Vérification des clés spécifiques avant d'utiliser un message par défaut
      if (
        error.error &&
        ( hasProperty(error.error, 'message') ||
          hasProperty(error.error, 'detail') ||
          hasProperty(error.error, 'hydra:description') ||
          hasProperty(error.error, 'error')
        )
      ) {
        return (
          error.error.message ||
          error.error.detail ||
          error.error['hydra:description'] ||
          error.error.error
        );
      }

      // Erreur côté serveur avec gestion des statuts HTTP
      if (!error.status) {
        return 'Une erreur inconnue est survenue.';
      }
      switch (error.status) {
        case 400:
          return 'Requête invalide. Veuillez vérifier les données envoyées.';
        case 401:
          return 'Non autorisé. Veuillez vous connecter.';
        case 403:
          return 'Accès refusé. Vous n’avez pas les permissions nécessaires.';
        case 404:
          return 'Ressource non trouvée.';
        case 500:
          return 'Une erreur interne est survenue. Veuillez réessayer plus tard.';
        default:
          return 'Une erreur inconnue est survenue.';
      }
    }
  }

  isError(response: any | ApiError): response is ApiError {
    return hasProperty(
      response,
      'isError',
      (value): value is boolean => typeof value === 'boolean'
    );
  }
}
